import { create, v, w } from '@dojo/framework/core/vdom';
import { icache } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { DateTime } from 'luxon';
import { timesync } from '@holmescorp/timesync';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { quizstate, quizidChange, quizChange, setProgression, setProgressionIsrunning, setProgressionWidget, setQuizSections, setActiveQuizSection } from '../support/state';
import { registry, createNlsMiddleware, userstore } from 'lcc-state';
import { logQuiz } from 'logger';
import { svc } from '../support/quizsvc';
import { svc as qfsvc } from '../support/qfsvc';
import { svc as prometricsvc } from '../support/prometricsvc';
import { svc as prometricmpesvc } from '../support/prometricmpe-svc';
import { svc as pearsonvuesvc } from '../support/pearsonvue-svc';
import { svc as flashcardsvc } from '../support/fc-svc';
import { svc as singlestepsvc } from '../support/ss-svc';
import css from '../css/quizfolder.m.css';
const progMap = new Map([
    ['flashcard', flashcardsvc],
    ['pearson-vue', pearsonvuesvc],
    ['prometric-mpe', prometricmpesvc],
    ['prometric', prometricsvc],
    ['question-feedback', qfsvc],
    ['singlestep', singlestepsvc]
]);
const nls = createNlsMiddleware('quiz');
const quizControl = createNlsMiddleware('quiz-section');
const factory = create({ icache, nls, quizControl, theme, quizstate }).properties();
export const ProgressionWrapper = factory(({ properties, middleware: { icache, nls, quizControl, theme, quizstate } }) => {
    const { store, quizkind: quizkindProp = null, quizid, key } = properties();
    quizstate.setStore(store);
    const { get, path } = quizstate;
    const { isPlaceholder } = nls();
    if (isPlaceholder) {
        return null;
    }
    const getQuizSection = (qid) => {
        console.log(`get section data for ${qid}`);
        const quizSection = quizControl();
        const qsStr = quizSection.messages[qid];
        const quizSections = qsStr && JSON.parse(qsStr);
        console.log('quizSection:', quizSections);
        quizstate.run(setQuizSections)({ quizSections });
        const getFinish = (timelimit) => {
            const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
            const quizmpe = userstore.get(userstore.path('mpe'));
            if (quizmpe && quizmpe[qid] !== undefined) {
                return DateTime.fromISO(quizmpe[qid].timeout);
            }
            return now.plus({ minutes: timelimit });
        };
        if (quizSections !== undefined) {
            const section = quizSections.sections[0];
            const timelimit = section.timelimit;
            const now = DateTime.fromJSDate(timesync.now()).setZone('utc');
            const sectionfinish = getFinish(timelimit);
            const timeout = sectionfinish.diff(now, ['hours', 'minutes', 'seconds']).toObject();
            console.log(`timeout for section 0: ${timeout} = ${sectionfinish}`);
            quizstate.run(setActiveQuizSection)({ cursor: 0, timeout, section });
        }
        return quizSections;
    };
    getQuizSection(quizid);
    const { loading } = theme.classes(css);
    const spinnerDiv = v('div', {
        key: `spinner-${key}`,
        classes: [loading],
        ...formatAriaProperties({ label: 'loading', live: 'assertive' })
    }, [v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })]);
    const quiz = get(path('quiz'));
    const quizkind = quiz ? quiz.kind : quizkindProp;
    const startphase = icache.getOrSet('startphase', 0);
    if (quizkind === null && startphase === 0) {
        icache.set('startphase', 1);
        (async () => {
            quizstate.run(quizidChange)({ quizid });
            const quizurl = get(path('quizurl'));
            svc._smd.url = quizurl;
            const v = await svc.progression();
            const { progression } = v;
            logQuiz.info(() => `setting up progression "${progression}"`);
            const prgsvc = (progMap.get(progression) || qfsvc);
            prgsvc._smd.url = quizurl;
            const quiz = await prgsvc.quiz();
            icache.set('startphase', 2);
            quizstate.run(quizChange)({ quiz });
            icache.set('proghold', v);
        })();
        return spinnerDiv;
    }
    if (startphase <= 1) {
        return spinnerDiv;
    }
    let progressionWidget = get(path('progressionWidget'));
    if (!progressionWidget) {
        const { quizkind = 'section' } = properties();
        const setupProgression = (v) => {
            const { progression } = v;
            let postfix = '';
            if (/topic\d+-\d+-0/.test(quizid) && !quizid.endsWith('-flashcards')) {
                postfix = '-topic';
            }
            else if (quizkind === 'pretest') {
                postfix = '-pretest';
            }
            else if (progression === 'question-feedback' && quizkind === 'posttest') {
                postfix = '-posttest';
            }
            const { get, path } = quizstate;
            const stopLoading = get(path('stopLoading'));
            stopLoading && stopLoading();
            icache.set('startphase', 3);
            quizstate.run(quizidChange)({ quizid });
            quizstate.run(setProgression)({ progression });
            quizstate.run(setProgressionIsrunning)({ isrunning: '' });
            progressionWidget = `${progression}${postfix}`;
            quizstate.run(setProgressionWidget)({ progressionWidget });
            logQuiz.info(() => `using progression widget ${progressionWidget}`);
        };
        const proghold = icache.get('proghold');
        if (proghold) {
            setupProgression(proghold);
            quizstate.run(quizidChange)({ quizid });
            const Progression = registry.get(progressionWidget);
            return Progression && w(Progression, { key: `progression-${key}`, quizstore: store });
        }
        else {
            (async () => {
                const stopLoading = get(path('stopLoading'));
                stopLoading && stopLoading();
                quizstate.run(setProgressionIsrunning)({ isrunning: '' });
                quizstate.run(quizidChange)({ quizid });
                svc._smd.url = get(path('quizurl'));
                const prg = await svc.progression();
                setupProgression(prg);
            })();
            return spinnerDiv;
        }
    }
    quizstate.run(quizidChange)({ quizid });
    const Progression = registry.get(progressionWidget);
    return Progression && w(Progression, { key: `progression-${key}`, quizstore: store });
});
